// Tu luyện (CULTIVATION §1, §4–§6): nối sim thuần với UI. Quyết toán closed-form lúc boot, khi tab
// hiện lại (Hồi Sơn nếu vắng ≥ 1 canh giờ), và tick online mỗi 1s khi tab đang hiện (§1.3).
// Slice đọc bằng peek(): tick 1s không kích autosave; lưu theo nhịp đồng hồ UI 30s, theo hành động
// và khi trang ẩn (persistence).
import { signal, computed, batch } from '@preact/signals';
import { BALANCE } from '@data/balance';
import { realmDef } from '@data/powerScale';
import { isSiteId } from '@data/content/sites';
import { isZoneId } from '@data/content/zones';
import { isTechniqueId } from '@data/content/techniques';
import {
  changeSite as simChangeSite,
  cultCost,
  deathSeclusionBlock,
  defaultCultivation,
  layerBlock,
  raiseTechniqueLayer,
  siteChangeBlock,
  startDeathSeclusion as simStartDeathSeclusion,
  stepInfo,
  MAX_STEP,
  qiRate,
  type CultivationState,
  type IdleSettleReport,
} from '@sim/cultivation';
import { HOUR_MS } from '@sim/core/time';
import { isPlainObject } from '@save/types';
import { registerSlice } from '../registry';
import { isHidden as hidden, now, onTick } from '../clock';
import { flush, markDirty, saveSeed } from '../persistence';
import { logLine } from './log';
import { isClaimed, ledger, markClaimed } from './ledger';
import { spend, wallet } from './wallet';
import { showReturn, type TrialSummary } from './returnSummary';
import { settleWorld } from '@sim/world/settle';
import {
  defaultTrial,
  reserveAt,
  type TrialCtx,
  type TrialLoot,
  type TrialState,
  type ZoneRuntime,
} from '@sim/trial';
import { TRIAL_ZONES, zoneDef } from '@data/content/zones';
import type { PillUse } from '@sim/crafting';
import type { ReceiveReport } from '@sim/items/bag';
import { emptyUtility } from '@sim/items/stats';

/** Nhịp quyết toán online (CULTIVATION §1.3). */
export const ONLINE_TICK_MS = 1000;
/** Khoảng trống giữa hai tick lớn hơn mức này (máy ngủ, JS bị treo) thì tính là vắng, không phải online. */
export const ONLINE_GAP_MAX_MS = 10_000;
export const RETURN_MIN_MS = BALANCE.RETURN_SUMMARY_MIN_HOURS * HOUR_MS;

const _cult = signal<CultivationState>(defaultCultivation(0));
const _trial = signal<TrialState>(defaultTrial(0));
export const trial = computed(() => _trial.value);

// ── Móc nối: Hành Trang cấp ngữ cảnh Lịch Luyện + nơi nhận loot; Động Phủ quyết toán theo cùng mốc.
export interface IdleHooks {
  trialCtx: (c: CultivationState, eff: number) => TrialCtx;
  loot: (loot: TrialLoot, online: boolean) => ReceiveReport | null | void;
}
let hooks: IdleHooks | null = null;
/** Đang ở trong batch của settleTo (chặn quyết toán lồng). */
let settling = false;
export function setIdleHooks(h: IdleHooks): void {
  hooks = h;
}
type SettleListener = (nowMs: number, cappedMs: number, online: boolean) => void;
const settleListeners: SettleListener[] = [];
/** Động Phủ đăng ký: công trình, lò đan, Dược Viên chạy theo cùng mốc quyết toán (trần 72h). */
export function onSettle(fn: SettleListener): void {
  settleListeners.push(fn);
}

const NO_CTX: TrialCtx = {
  playerL: 1,
  realmIdx: 0,
  atk: 0,
  utility: emptyUtility(),
  eff: 0,
};
const _pulse = signal(0);

export const cultivation = computed(() => _cult.value);
/** Tăng mỗi lần đột phá tiểu cảnh: view rung thanh, đèn sáng lên một nhịp. */
export const breakthroughPulse = computed(() => _pulse.value);
/** Đăng Căn đã thành (cảnh A7): trước đó không hấp thụ được linh khí. */
export const lampRootFormed = computed(() => ledger.value.flags.t1_dang_can_formed === true);

/** Linh khí / giây đang hấp thụ khi online (đã nhân Tử Quan). */
export const onlineRate = computed(() => {
  if (!lampRootFormed.value) return 0;
  const c = _cult.value;
  const k = c.idle.seclusion.kind === 'death' ? BALANCE.DEATH_SECLUSION_MULT : 1;
  const share = c.idle.mode === 'trial' ? BALANCE.TRIAL_QI_SHARE : 1;
  return qiRateAt(c) * k * share;
});

function qiRateAt(c: CultivationState): number {
  return qiRate(c, c.idle.lastSettledMs);
}

export function isAtPeak(c: CultivationState): boolean {
  return stepInfo(c.step).isLast && c.cultivationXp >= cultCost(c.step) - 1e-9;
}

// ── Ghi Truyền Âm và juice theo bản kê. Mọi dòng mang mốc lúc quyết toán (Truyền Âm xếp theo thứ tự
// ghi, không chèn dòng lùi giờ vào giữa sổ).
function applyReportEffects(before: CultivationState, after: CultivationState, r: IdleSettleReport): void {
  for (const b of r.minorBreakthroughs) {
    const info = stepInfo(b.toStep);
    logLine('cultivation', `log.cult.minorBreak.${realmDef(info.realm).id}`, { s: info.s }, r.toMs);
    // Lần đột phá đầu (STORY A9, seed_15): một dòng không ai nói, rồi Đăng Linh hỏi lại.
    if (!isClaimed('story.firstBreakWhisper')) {
      markClaimed('story.firstBreakWhisper');
      logLine('cultivation', 'story.a9.whisper', undefined, r.toMs);
      logLine('cultivation', 'story.a9.lamp', undefined, r.toMs);
    }
  }
  if (r.minorBreakthroughs.length > 0) _pulse.value = _pulse.value + 1;
  if (!isAtPeak(before) && isAtPeak(after)) logLine('cultivation', 'log.cult.peak');
  for (const k of r.worldNews) logLine('world', k);
  for (const k of r.rareLines) logLine('cultivation', k);
  if (r.deathSeclusionEndedAtMs !== null)
    logLine('cultivation', 'log.cult.deathSeclusionEnd', undefined, r.toMs);
}

/** Quyết toán tới `nowMs`. Trước khi thành Đăng Căn chỉ dời mốc (không có gì để hấp thụ). */
export function settleTo(nowMs: number, online: boolean): IdleSettleReport | null {
  if (settling) return null;
  const c = _cult.peek();
  if (nowMs < c.idle.lastSettledMs) {
    // Giờ máy lùi (hoặc save mang mốc tương lai): dời mốc về hiện tại, không kẹt tới khi giờ thật đuổi kịp.
    _cult.value = { ...c, idle: { ...c.idle, lastSettledMs: nowMs } };
    return null;
  }
  if (nowMs === c.idle.lastSettledMs) return null;
  if (!lampRootFormed.peek()) {
    _cult.value = { ...c, idle: { ...c.idle, lastSettledMs: nowMs } };
    return null;
  }
  const w = settleWorld(
    { cult: c, trial: _trial.peek() },
    c.idle.lastSettledMs,
    nowMs,
    saveSeed(),
    online,
    (cc, eff) => (hooks ? hooks.trialCtx(cc, eff) : NO_CTX),
  );
  const state = w.world.cult;
  const report = w.report;
  // Loot tự mặc trong lúc quyết toán đổi phần trang bị (setGearBonus): không quyết toán lồng lần hai
  // (listener Động Phủ của lần lồng sẽ chạy trước phần dời trần 72h của lần ngoài). Đồ mới tính từ mốc này.
  settling = true;
  try {
    batch(() => {
      _cult.value = state;
      _trial.value = w.world.trial;
      applyReportEffects(c, state, report);
      let rr: ReceiveReport | null | void = null;
      if (
        w.loot.kills > 0 ||
        w.loot.items.length ||
        Object.keys(w.loot.goods).length ||
        Object.keys(w.loot.currency).length
      )
        rr = hooks?.loot(w.loot, online);
      lastLoot = summarize(w.loot, rr ?? null);
      for (const fn of settleListeners) fn(nowMs, report.cappedMs, online);
    });
  } finally {
    settling = false;
  }
  if (report.minorBreakthroughs.length > 0 || report.deathSeclusionEndedAtMs !== null) markDirty();
  return report;
}

let lastLoot: TrialSummary | null = null;
function summarize(l: TrialLoot, r: ReceiveReport | null): TrialSummary | null {
  if (l.kills === 0 && l.items.length === 0 && Object.keys(l.goods).length === 0 && !l.currency.spiritStone)
    return null;
  return {
    kills: l.kills,
    items: l.items.length,
    equipped: r?.equipped.length ?? 0,
    salvaged: r?.salvagedCount ?? 0,
    salvagedFull: r?.salvagedFull ?? 0,
    stones: l.currency.spiritStone ?? 0,
    sealed: l.items.filter((i) => i.rarity === 'immortal' || i.rarity === 'chaos').length,
    goods: Object.values(l.goods).reduce((a, b) => a + b, 0),
  };
}

/**
 * Trở lại sau một quãng vắng (boot, tab hiện lại, máy thức dậy): quyết toán offline; vắng ≥ 1 canh
 * giờ thì dựng Hồi Sơn từ đúng bản kê này.
 */
export function resumeFromAbsence(nowMs: number = now()): void {
  const before = _cult.peek();
  const absentMs = nowMs - before.idle.lastSettledMs;
  const report = settleTo(nowMs, false);
  if (report && absentMs >= RETURN_MIN_MS)
    showReturn({ report, absentMs, stepBefore: before.step, stepAfter: _cult.peek().step, trial: lastLoot });
  markDirty();
}

/** Hook dev `window.__return.simulate(ms)` (UI_SPEC §16): coi như vừa vắng `ms` rồi trở lại. */
export function simulateAbsence(ms: number): void {
  const n = now();
  settleTo(n, true);
  const c = _cult.peek();
  _cult.value = { ...c, idle: { ...c.idle, lastSettledMs: Math.max(0, n - ms) } };
  resumeFromAbsence(n);
}

/** Hook dev: dời mốc quyết toán lùi `ms` (như đã đóng app từ `ms` trước) rồi lưu ngay; không quyết toán. */
export function rewindForDev(ms: number): Promise<void> {
  const c = _cult.peek();
  _cult.value = { ...c, idle: { ...c.idle, lastSettledMs: c.idle.lastSettledMs - ms } };
  return flush();
}

function onlineTick(): void {
  if (hidden()) return;
  const n = now();
  if (n - _cult.peek().idle.lastSettledMs > ONLINE_GAP_MAX_MS) resumeFromAbsence(n);
  else settleTo(n, true);
}

function onVisibility(): void {
  if (hidden()) settleTo(now(), true);
  else resumeFromAbsence(now());
}

let loopStop: (() => void) | null = null;

/** Bật vòng idle online: tick 1s khi tab hiện, xử lý ẩn / hiện, lưu theo nhịp 30s. */
export function startIdleLoop(): () => void {
  loopStop?.();
  const id = setInterval(onlineTick, ONLINE_TICK_MS);
  document.addEventListener('visibilitychange', onVisibility);
  const offTick = onTick(() => markDirty());
  loopStop = () => {
    clearInterval(id);
    document.removeEventListener('visibilitychange', onVisibility);
    offTick();
    loopStop = null;
  };
  return loopStop;
}

// ── Hành động của người chơi (mỗi hành động quyết toán tới hiện tại trước khi đổi trạng thái).

/** Cảnh A7: Đăng Căn thành, tu luyện bắt đầu từ lúc này. */
export function beginCultivation(nowMs: number = now()): void {
  const c = _cult.peek();
  _cult.value = { ...c, idle: { ...c.idle, lastSettledMs: nowMs } };
  logLine('cultivation', 'log.cult.begin', undefined, nowMs);
  markDirty();
}

export function startDeathSeclusion(hours: number): boolean {
  const n = now();
  settleTo(n, true);
  const c = _cult.peek();
  if (!lampRootFormed.peek() || deathSeclusionBlock(c, hours) !== null) return false;
  const ds = simStartDeathSeclusion(c, hours, n);
  _cult.value = { ...ds, idle: { ...ds.idle, mode: 'seclusion', trialZoneId: null } };
  logLine('cultivation', 'log.cult.deathSeclusionStart', { h: hours / 2 }, n);
  void flush();
  return true;
}

export function changeSite(siteId: string): boolean {
  const n = now();
  settleTo(n, true);
  const c = _cult.peek();
  if (siteChangeBlock(c, siteId) !== null) return false;
  _cult.value = simChangeSite(c, siteId);
  logLine('cultivation', 'log.cult.siteChanged', { site: `@${siteId}.name` }, n);
  markDirty();
  return true;
}

export function raiseLayer(): boolean {
  settleTo(now(), true);
  const c = _cult.peek();
  const stones = wallet.peek().spiritStone;
  if (layerBlock(c, stones) !== null) return false;
  const r = raiseTechniqueLayer(c, stones);
  let ok = false;
  batch(() => {
    ok = spend({ spiritStone: r.stonesSpent });
    if (ok) {
      _cult.value = r.state;
      logLine('cultivation', 'log.cult.layerUp', { n: r.state.technique.layer });
    }
  });
  if (ok) void flush();
  return ok;
}

/** Giao Lịch Luyện ở một vùng (ô idle chuyển sang Lịch Luyện). Tử Quan thì không xuất quan sớm. */
export type TrialBlock = 'deathSeclusion' | 'unknown' | 'same' | 'noLampRoot';
export function trialBlock(zoneId: string): TrialBlock | null {
  const c = _cult.peek();
  if (!lampRootFormed.peek()) return 'noLampRoot';
  if (!TRIAL_ZONES.some((z) => z.id === zoneId)) return 'unknown';
  if (c.idle.seclusion.kind === 'death') return 'deathSeclusion';
  if (c.idle.mode === 'trial' && c.idle.trialZoneId === zoneId) return 'same';
  return null;
}

export function startTrial(zoneId: string): boolean {
  const n = now();
  settleTo(n, true);
  if (trialBlock(zoneId) !== null) return false;
  const c = _cult.peek();
  _cult.value = { ...c, idle: { ...c.idle, mode: 'trial', trialZoneId: zoneId } };
  logLine('cultivation', 'log.trial.start', { zone: `@${zoneId}.name` }, n);
  void flush();
  return true;
}

export function stopTrial(): boolean {
  const n = now();
  settleTo(n, true);
  const c = _cult.peek();
  if (c.idle.mode !== 'trial') return false;
  _cult.value = { ...c, idle: { ...c.idle, mode: 'seclusion', trialZoneId: null } };
  logLine('cultivation', 'log.trial.stop', undefined, n);
  void flush();
  return true;
}

/** Trữ Lượng hiện tại (0..100) của một vùng, tính lười tới `atMs`. */
export function zoneReserve(zoneId: string, atMs: number = now()): number {
  const rt: ZoneRuntime | undefined = _trial.value.zones[zoneId];
  const z = zoneDef(zoneId);
  return rt ? reserveAt(rt, z, atMs) : 100;
}

/**
 * Hành Trang báo phần trang bị cộng vào tu luyện khi đổi đồ: quyết toán tới hiện tại bằng đồ cũ rồi
 * mới đổi. `settle: false` (lúc nạp save) chỉ gán: quãng vắng còn chờ resumeFromAbsence tính bằng hiệu
 * suất offline + Hồi Sơn. Đang ở giữa một lần quyết toán thì cũng chỉ gán.
 */
export function setGearBonus(g: CultivationState['gear'], o: { settle?: boolean } = {}): void {
  const c = _cult.peek();
  if (c.gear.qiRatePct === g.qiRatePct && c.gear.offlineEffPct === g.offlineEffPct) return;
  if (o.settle !== false && !settling) settleTo(now(), true);
  _cult.value = { ..._cult.peek(), gear: g };
}

/**
 * Uống đan (Hành Trang gọi) ở mốc `atMs`: bể buff cùng loại không cộng, làm mới thời gian; đan độc lấy
 * `u.toxAfter`. Người gọi quyết toán tới `atMs` TRƯỚC khi tính `u` (đan độc đã luyện hóa tới lúc uống).
 */
export function applyPillUse(u: PillUse, atMs: number = now()): void {
  settleTo(atMs, true);
  const c = _cult.peek();
  const buffs = u.buff
    ? [...c.buffs.filter((b) => b.id !== u.buff!.id && b.untilMs > atMs), u.buff]
    : c.buffs;
  _cult.value = { ...c, buffs, pillToxicity: u.toxAfter };
}

// ── Save (core.cultivation, từ v3). Làm sạch từng trường; thiếu / sai thì về mặc định.
function num(v: unknown, d: number, min = -Infinity, max = Infinity): number {
  return typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : d;
}

export function sanitizeCultivation(d: unknown, nowMs: number): CultivationState {
  const def = defaultCultivation(nowMs);
  if (!isPlainObject(d)) return def;
  const idle = isPlainObject(d.idle) ? d.idle : {};
  const sec = isPlainObject(idle.seclusion) ? idle.seclusion : {};
  const life = isPlainObject(d.lifespan) ? d.lifespan : {};
  const tech = isPlainObject(d.technique) ? d.technique : {};
  const tri = isPlainObject(tech.triOrigin) ? tech.triOrigin : {};
  // Tử Quan chỉ hợp lệ khi hạn là số hữu hạn, không xa hơn thời lượng dài nhất (save hỏng không kẹt mãi).
  const maxEnd = nowMs + Math.max(...BALANCE.DEATH_SECLUSION_DURATIONS_H) * HOUR_MS;
  const endsAt = num(sec.endsAtMs, NaN, 0, maxEnd);
  const death = sec.kind === 'death' && !Number.isNaN(endsAt);
  const known = Array.isArray(tech.known) ? tech.known.filter(isTechniqueId) : [];
  const mainId = isTechniqueId(tech.mainId) ? tech.mainId : def.technique.mainId;
  return {
    step: Math.floor(num(d.step, 0, 0, MAX_STEP)),
    cultivationXp: num(d.cultivationXp, 0, 0),
    overflowQi: num(d.overflowQi, 0, 0),
    idle: {
      mode:
        idle.mode === 'trial' && isZoneId(idle.trialZoneId) && sec.kind !== 'death' ? 'trial' : 'seclusion',
      trialZoneId: isZoneId(idle.trialZoneId) ? idle.trialZoneId : null,
      locationId: isSiteId(idle.locationId) ? idle.locationId : def.idle.locationId,
      seclusion: death
        ? {
            kind: 'death',
            startedAtMs: num(sec.startedAtMs, nowMs),
            endsAtMs: endsAt,
            lifespanPrepaid: num(sec.lifespanPrepaid, 0, 0),
          }
        : { kind: 'light', startedAtMs: num(sec.startedAtMs, nowMs), endsAtMs: null, lifespanPrepaid: 0 },
      lastSettledMs: Math.min(num(idle.lastSettledMs, nowMs), nowMs),
    },
    lifespan: {
      age: num(life.age, def.lifespan.age, 0),
      lifespanBonus: num(life.lifespanBonus, 0, 0),
      pillsTakenByRealm: def.lifespan.pillsTakenByRealm.map((z, i) =>
        Array.isArray(life.pillsTakenByRealm) ? num(life.pillsTakenByRealm[i], z, 0) : z,
      ),
      history: Array.isArray(life.history)
        ? life.history.filter(
            (h): h is CultivationState['lifespan']['history'][number] =>
              isPlainObject(h) &&
              typeof h.atMs === 'number' &&
              typeof h.delta === 'number' &&
              typeof h.source === 'string',
          )
        : [],
    },
    technique: {
      mainId,
      layer: Math.floor(num(tech.layer, 1, 1, 9)),
      mastery: num(tech.mastery, 0, 0),
      known: known.includes(mainId) ? known : [mainId, ...known],
      triOrigin: { body: num(tri.body, 0, 0), spirit: num(tri.spirit, 0, 0), mind: num(tri.mind, 0, 0) },
    },
    gatherArrayLv: Math.floor(num(d.gatherArrayLv, 0, 0, 10)),
    pillToxicity: num(d.pillToxicity, 0, 0, BALANCE.PILL_TOXICITY_MAX),
    gear: {
      qiRatePct: num(isPlainObject(d.gear) ? d.gear.qiRatePct : 0, 0, 0),
      offlineEffPct: num(isPlainObject(d.gear) ? d.gear.offlineEffPct : 0, 0, 0),
    },
    buffs: Array.isArray(d.buffs)
      ? d.buffs.filter(
          (b): b is CultivationState['buffs'][number] =>
            isPlainObject(b) &&
            typeof b.id === 'string' &&
            typeof b.bucket === 'number' &&
            typeof b.untilMs === 'number',
        )
      : [],
  };
}

registerSlice<CultivationState>({
  key: 'cultivation',
  scope: 'core',
  serialize: () => _cult.peek(),
  hydrate: (d) => {
    _cult.value = sanitizeCultivation(d, now());
  },
});

function sanitizeTrial(d: unknown, nowMs: number): TrialState {
  const def = defaultTrial(nowMs);
  if (!isPlainObject(d)) return def;
  const zones = { ...def.zones };
  if (isPlainObject(d.zones))
    for (const z of TRIAL_ZONES) {
      const r = d.zones[z.id];
      if (!isPlainObject(r)) continue;
      const frac: Record<string, number> = {};
      if (isPlainObject(r.gatherFrac))
        for (const [k, v] of Object.entries(r.gatherFrac)) frac[k] = num(v, 0, 0, 1);
      zones[z.id] = {
        reserve: num(r.reserve, 100, 0, 100),
        atMs: Math.min(num(r.atMs, nowMs), nowMs),
        gatherFrac: frac,
      };
    }
  const kills: Record<string, number> = {};
  if (isPlainObject(d.kills)) for (const z of TRIAL_ZONES) kills[z.id] = Math.floor(num(d.kills[z.id], 0, 0));
  return { zones, kills, killFrac: num(d.killFrac, 0, 0, 1) };
}

registerSlice<TrialState>({
  key: 'trial',
  scope: 'core',
  serialize: () => _trial.peek(),
  hydrate: (d) => {
    _trial.value = sanitizeTrial(d, now());
  },
});
